/**
 * Shared mappings for E component abstraction layer
 *
 * These mappings ensure consistent translation between:
 * - Buefy (Bulma-based) styling
 * - PrimeVue (Tailwind-based) styling
 *
 * This allows us to keep the same API across all E components while
 * translating to the appropriate styling system under the hood.
 */

/**
 * Color/Type mappings
 * Maps common color types to their Tailwind equivalents
 */
export const colorMap = {
    'is-primary': 'tw:text-blue-600',
    'is-success': 'tw:text-green-600',
    'is-danger': 'tw:text-red-600',
    'is-warning': 'tw:text-yellow-600',
    'is-info': 'tw:text-cyan-600',
    'is-link': 'tw:text-blue-500',
    'is-dark': 'tw:text-gray-900',
    'is-light': 'tw:text-gray-300',
    'is-white': 'tw:text-white',
    'is-black': 'tw:text-black',
    // Non-prefixed versions (for flexibility)
    primary: 'tw:text-blue-600',
    success: 'tw:text-green-600',
    danger: 'tw:text-red-600',
    warning: 'tw:text-yellow-600',
    info: 'tw:text-cyan-600',
    link: 'tw:text-blue-500',
    dark: 'tw:text-gray-900',
    light: 'tw:text-gray-300',
    white: 'tw:text-white',
    black: 'tw:text-black',
};

/**
 * Background color mappings
 * Maps common color types to their Tailwind background equivalents
 */
export const bgColorMap = {
    'is-primary': 'tw:bg-blue-600',
    'is-success': 'tw:bg-green-600',
    'is-danger': 'tw:bg-red-600',
    'is-warning': 'tw:bg-yellow-600',
    'is-info': 'tw:bg-cyan-600',
    'is-link': 'tw:bg-blue-500',
    'is-dark': 'tw:bg-gray-900',
    'is-light': 'tw:bg-gray-300',
    'is-white': 'tw:bg-white',
    'is-black': 'tw:bg-black',
    // Non-prefixed versions
    primary: 'tw:bg-blue-600',
    success: 'tw:bg-green-600',
    danger: 'tw:bg-red-600',
    warning: 'tw:bg-yellow-600',
    info: 'tw:bg-cyan-600',
    link: 'tw:bg-blue-500',
    dark: 'tw:bg-gray-900',
    light: 'tw:bg-gray-300',
    white: 'tw:bg-white',
    black: 'tw:bg-black',
};

/**
 * Size mappings
 * Maps Buefy size classes to Tailwind text size classes
 */
export const sizeMap = {
    'is-small': 'tw:text-sm',
    'is-medium': 'tw:text-2xl',
    'is-large': 'tw:text-4xl',
    // Non-prefixed versions
    small: 'tw:text-sm',
    medium: 'tw:text-2xl',
    large: 'tw:text-4xl',
};

/**
 * Icon size mappings
 * More granular sizes for icons specifically
 */
export const iconSizeMap = {
    'is-small': 'tw:text-md',
    'is-medium': 'tw:text-3xl',
    'is-large': 'tw:text-5xl',
    small: 'tw:text-md',
    medium: 'tw:text-2xl',
    large: 'tw:text-4xl',
};

/**
 * Button size mappings
 * Maps to Tailwind padding and text size combinations
 */
export const buttonSizeMap = {
    'is-small': 'tw:px-3 tw:py-2 tw:text-md',
    'is-medium': 'tw:px-4 tw:py-3 tw:text-base',
    'is-large': 'tw:px-6 tw:py-4 tw:text-lg',
    small: 'tw:px-3 tw:py-2 tw:text-md',
    medium: 'tw:px-4 tw:py-3 tw:text-base',
    large: 'tw:px-6 tw:py-4 tw:text-lg',
};

/**
 * Helper function to get Tailwind color class
 * @param {string} type - The color type (e.g., 'is-danger', 'success')
 * @param {string} defaultClass - Default class if type is not found
 * @returns {string} Tailwind color class
 */
export function getColorClass(type, defaultClass = '') {
    return colorMap[type] || defaultClass;
}

/**
 * Helper function to get Tailwind background color class
 * @param {string} type - The color type (e.g., 'is-danger', 'success')
 * @param {string} defaultClass - Default class if type is not found
 * @returns {string} Tailwind background color class
 */
export function getBgColorClass(type, defaultClass = '') {
    return bgColorMap[type] || defaultClass;
}

/**
 * Helper function to get Tailwind size class
 * @param {string} size - The size (e.g., 'is-large', 'small')
 * @param {string} defaultClass - Default class if size is not found
 * @returns {string} Tailwind size class
 */
export function getSizeClass(size, defaultClass = 'tw:text-base') {
    return sizeMap[size] || defaultClass;
}

/**
 * Helper function to get Tailwind icon size class
 * @param {string} size - The size (e.g., 'is-large', 'small')
 * @param {string} defaultClass - Default class if size is not found
 * @returns {string} Tailwind icon size class
 */
export function getIconSizeClass(size, defaultClass = 'tw:text-2xl') {
    return iconSizeMap[size] || defaultClass;
}

/**
 * Helper function to get Tailwind button size classes
 * @param {string} size - The size (e.g., 'is-large', 'small')
 * @param {string} defaultClass - Default class if size is not found
 * @returns {string} Tailwind button size classes
 */
export function getButtonSizeClass(size, defaultClass = 'tw:px-4 tw:py-2 tw:text-base') {
    return buttonSizeMap[size] || defaultClass;
}

/**
 * PrimeVue Button severity mappings
 * Maps Buefy button types to PrimeVue Button severity values
 */
export const primeVueSeverityMap = {
    'is-primary': 'primary',
    'is-success': 'success',
    'is-danger': 'danger',
    'is-warning': 'warn',
    'is-info': 'info',
    'is-link': 'secondary',
    'is-dark': 'secondary',
    /** Muted / inactive labels (e.g. disabled); prefer over `is-light` which maps to high-contrast. */
    'is-secondary': 'secondary',
    'is-light': 'contrast',
    // Non-prefixed versions
    primary: 'primary',
    secondary: 'secondary',
    success: 'success',
    danger: 'danger',
    warning: 'warn',
    info: 'info',
    light: 'contrast',
};

/**
 * Normalize Bulma compound types like "is-success is-light" for severity mapping.
 * The old replace() that turned is-light into "light" glued tokens into "is-successlight",
 * which does not map and forced PrimeVue tags to primary (blue).
 *
 * Also strips non-severity helpers (e.g. "is-clickable" from AttendanceRecorderCell) so
 * "is-success is-light is-clickable" resolves to "is-success", not an unmapped compound string.
 *
 * @param {string|null|undefined} type
 * @returns {string|null}
 */
export function bulmaTypeForPrimeVueSeverity(type) {
    if (!type) {
        return null;
    }

    const parts = type.trim().split(/\s+/).filter(Boolean);

    const mapped = parts.filter(part => Object.hasOwn(primeVueSeverityMap, part));
    const nonLight = mapped.filter(part => part !== 'is-light');

    if (nonLight.length > 0) {
        return nonLight[0];
    }

    if (mapped.includes('is-light')) {
        return 'is-light';
    }

    return 'is-light';
}

/**
 * Helper function to map Buefy button type to PrimeVue severity
 * @param {string} type - The Buefy button type (e.g., 'is-primary', 'success')
 * @param {string} defaultSeverity - Default severity if type is not found
 * @returns {string} PrimeVue severity value
 */
export function getPrimeVueSeverity(type, defaultSeverity = 'primary') {
    return primeVueSeverityMap[type] || defaultSeverity;
}

/**
 * Icon name mappings
 * Maps Buefy's internal icon names to Unicons icon names
 * This mirrors the internalIcons mapping in the Buefy config
 */
export const iconNameMap = {
    check: 'check',
    information: 'info-circle',
    'check-circle': 'check-circle',
    alert: 'exclamation-triangle',
    'alert-circle': 'exclamation-circle',
    'arrow-up': 'angle-up',
    'chevron-right': 'angle-right',
    'chevron-left': 'angle-left',
    'chevron-down': 'angle-down',
    eye: 'eye',
    'eye-off': 'eye-slash',
    'menu-down': 'angle-down',
    'menu-up': 'angle-up',
    'close-circle': 'times',
};

/**
 * Helper function to get Unicons icon class with proper name translation
 * @param {string} iconName - The Buefy icon name (e.g., 'information', 'alert')
 * @returns {string} Full Unicons icon class (e.g., 'uil uil-info-circle')
 */
export function getUniconClass(iconName) {
    if (!iconName) return '';

    // Translate Buefy internal icon names to Unicons names
    const uniconName = iconNameMap[iconName] || iconName;

    return `uil uil-${uniconName}`;
}

/**
 * Tag size mappings for PrimeVue
 * Maps Buefy size classes to Tailwind classes
 */
export const tagSizeMap = {
    'is-small': 'tw:text-xs tw:px-2 tw:py-1',
    'is-medium': 'tw:text-base tw:px-3 tw:py-2',
    'is-large': 'tw:text-xl tw:px-4 tw:py-3',
    // Non-prefixed versions
    small: 'tw:text-xs tw:px-2 tw:py-1',
    medium: 'tw:text-base tw:px-3 tw:py-2',
    large: 'tw:text-xl tw:px-4 tw:py-3',
};

/**
 * Helper function to get Tailwind size classes for tags
 * @param {string} size - The size (e.g., 'is-small', 'medium')
 * @returns {string} Tailwind size classes
 */
export function getTagSizeClass(size) {
    return tagSizeMap[size] || '';
}

/**
 * PrimeVue Input size mappings
 * Maps Buefy size classes to PrimeVue size prop values
 */
export const primeVueInputSizeMap = {
    'is-small': 'small',
    'is-large': 'large',
    'is-medium': undefined, // PrimeVue doesn't have medium, it's the default
    // Non-prefixed versions
    small: 'small',
    large: 'large',
    medium: undefined,
};

/**
 * Helper function to map Buefy input size to PrimeVue size
 * @param {string} size - The Buefy size (e.g., 'is-small', 'large')
 * @returns {string|undefined} PrimeVue size value or undefined for default
 */
export function getPrimeVueInputSize(size) {
    return primeVueInputSizeMap[size];
}

/**
 * Helper function to map common Buefy props to PrimeVue props for input components
 * @param {object} attrs - Attributes object from $attrs
 * @returns {object} Mapped props for PrimeVue
 */
export function mapInputPropsToPrimeVue(attrs) {
    const mapped = { ...attrs };

    // Remove type - handled by component selection
    delete mapped.type;

    // Map size
    if (mapped.size) {
        const primeVueSize = getPrimeVueInputSize(mapped.size);
        if (primeVueSize) {
            mapped.size = primeVueSize;
        } else {
            delete mapped.size; // Remove if no mapping (use default)
        }
    }

    // Map maxlength to maxLength (camelCase)
    if (mapped.maxlength !== undefined) {
        mapped.maxLength = mapped.maxlength;
        delete mapped.maxlength;
    }

    // Remove Buefy-specific props that don't apply to PrimeVue
    delete mapped['password-reveal'];
    delete mapped.icon;
    delete mapped['icon-right'];
    delete mapped['icon-right-clickable'];

    return mapped;
}
